(() => {
  const init = (root = document) => root.querySelectorAll('[data-kgmj]').forEach((section) => {
    if (section.dataset.ready === 'true') return;
    section.dataset.ready = 'true';

    const arrival = section.querySelector('[data-kgmj-arrival]');
    const foyer = section.querySelector('[data-kgmj-foyer]');
    const explorer = section.querySelector('[data-kgmj-explorer]');
    const arrivalVideo = section.querySelector('.kgmj-arrival__video');
    const arrivalProgress = section.querySelector('[data-kgmj-arrival-progress]');
    const status = section.querySelector('[data-kgmj-status]');
    const scenes = [...section.querySelectorAll('[data-kgmj-scene]')];
    const products = [...section.querySelectorAll('[data-kgmj-product]')];
    const roomTitle = section.querySelector('[data-kgmj-room-title]');
    const roomNumber = section.querySelector('[data-kgmj-room-number]');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const roomState = new Map();
    let activeIndex = 0;
    let arrivalFallback = 0;
    let lastHotspot = null;
    let arrivalScrollLock = -10000;

    const FULLY_ZOOMED_OUT = 0.68;
    const stateForScene = scene => ({
      yaw:Number(scene.dataset.initialYaw ?? (scene.dataset.kgmjScene === 'living' ? 54 : 0)),
      pitch:Number(scene.dataset.initialPitch ?? 0),
      zoom:Number(scene.dataset.initialZoom ?? FULLY_ZOOMED_OUT)
    });
    scenes.forEach(scene => roomState.set(scene.dataset.kgmjScene, stateForScene(scene)));
    const announce = text => { if (status) status.textContent = text; };
    const norm = a => { a = ((a + 180) % 360 + 360) % 360 - 180; return a; };
    const clamp = (v,min,max) => Math.max(min,Math.min(max,v));

    const PRODUCT_MODEL_START_ORBIT = '35deg 66deg 180%';
    const PRODUCT_MODEL_MIN_ORBIT = 'auto 35deg 70%';
    const PRODUCT_MODEL_MAX_ORBIT = 'auto 85deg 180%';
    const PRODUCT_MODEL_AUTOROTATE_DELAY = 1500;

    const configureProductModelViewer = model => {
      if (!model) return;

      model.setAttribute('camera-controls', '');
      model.setAttribute('auto-rotate', '');
      model.setAttribute('auto-rotate-delay', String(PRODUCT_MODEL_AUTOROTATE_DELAY));
      model.setAttribute('rotation-per-second', '16deg');
      model.setAttribute('camera-target', 'auto auto auto');
      model.setAttribute('camera-orbit', PRODUCT_MODEL_START_ORBIT);
      model.setAttribute('min-camera-orbit', PRODUCT_MODEL_MIN_ORBIT);
      model.setAttribute('max-camera-orbit', PRODUCT_MODEL_MAX_ORBIT);
      model.setAttribute('interaction-prompt', 'none');

      const resetCamera = () => {
        model.setAttribute('camera-orbit', PRODUCT_MODEL_START_ORBIT);
        model.setAttribute('camera-target', 'auto auto auto');
        try { model.jumpCameraToGoal?.(); } catch (_) {}
      };

      if (model.loaded) resetCamera();
      else model.addEventListener('load', resetCamera, { once:true });
    };

    const prepareProductViewer = panel => {
      panel?.querySelectorAll?.('model-viewer').forEach(configureProductModelViewer);
    };

    section.querySelectorAll('model-viewer').forEach(configureProductModelViewer);


    const setStage = stage => {
      [arrival, foyer, explorer].forEach(el => {
        if (!el) return;
        const on = el === stage;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-hidden', String(!on));
      });
    };

    const closeProducts = (restoreFocus = false) => {
      products.forEach(p => { p.classList.remove('is-open'); p.setAttribute('aria-hidden','true'); });
      section.classList.remove('has-product-open');
      document.documentElement.classList.remove('kgmj-product-lock');
      document.body?.classList.remove('kgmj-product-lock');
      if (restoreFocus && lastHotspot?.isConnected) lastHotspot.focus({preventScroll:true});
    };

    const startArrival = () => {
      closeProducts();
      clearTimeout(arrivalFallback);
      setStage(arrival);
      announce('KGM Maison cinematic entrance. Scroll down at any time or choose Skip intro.');
      section.scrollIntoView({behavior:'smooth', block:'start'});
      if (!arrivalVideo || reduce) return;
      arrivalVideo.pause?.();
      try { arrivalVideo.currentTime = 0; } catch (_) {}
      arrivalVideo.play?.().catch(()=>{});
    };

    const showFoyer = () => {
      clearTimeout(arrivalFallback);
      closeProducts();
      arrivalVideo?.pause?.();
      setStage(foyer);
      announce('KGM Maison foyer. Choose a room.');
    };

    const scrollPastJourney = () => {
      const now = performance.now();
      if (now - arrivalScrollLock < 400) return;
      arrivalScrollLock = now;
      const next = section.nextElementSibling;
      if (next) next.scrollIntoView({behavior:'smooth', block:'start'});
      else window.scrollBy({top: Math.max(window.innerHeight * .82, 560), behavior:'smooth'});
    };

    if (arrivalVideo) {
      arrivalVideo.addEventListener('timeupdate', () => {
        if (!arrivalVideo.duration || !arrivalProgress) return;
        const local = clamp(arrivalVideo.currentTime / arrivalVideo.duration, 0, 1);
        arrivalProgress.style.width = `${local * 100}%`;
      });
      arrivalVideo.addEventListener('ended', () => {
        if (arrivalProgress) arrivalProgress.style.width = '100%';
        showFoyer();
      });
      arrivalVideo.addEventListener('error', () => {
        announce('Cinematic video unavailable. Choose Skip intro to enter the Maison.');
      });
    }

    if (section.dataset.autoIntro === 'true' && !reduce) {
      window.setTimeout(startArrival, 50);
    } else if (reduce) {
      arrivalVideo?.pause?.();
    }

    // The intro never traps the page. Capture wheel events anywhere over the first stage.
    section.addEventListener('wheel', e => {
      if (!arrival?.classList.contains('is-active') || e.deltaY <= 8) return;
      e.preventDefault();
      scrollPastJourney();
    }, {passive:false, capture:true});
    let introTouchY = null;
    arrival?.addEventListener('touchstart', e => { introTouchY = e.touches?.[0]?.clientY ?? null; }, {passive:true});
    arrival?.addEventListener('touchend', e => {
      if (introTouchY == null) return;
      const y = e.changedTouches?.[0]?.clientY ?? introTouchY;
      if (introTouchY - y > 45) scrollPastJourney();
      introTouchY = null;
    }, {passive:true});

    const updateHotspots = scene => {
      // Every upgraded room uses the shared spherical projector.
      if (scene.querySelector('[data-kgmj-sphere="true"]')) return;
      const state = roomState.get(scene.dataset.kgmjScene);
      scene.querySelectorAll('.kgmj-hotspot').forEach(h => {
        const hy = Number(h.dataset.yaw || 0);
        const hp = Number(h.dataset.pitch || 0);
        const delta = norm(hy - state.yaw);
        const visible = Math.abs(delta) <= 74;
        h.hidden = !visible;
        if (!visible) return;
        const x = 50 + (delta / 74) * 46;
        const y = clamp(51 + (hp - state.pitch) * 1.45, 16, 83);
        const scale = 1 - Math.min(Math.abs(delta)/160,.22);
        h.style.left = `${x}%`;
        h.style.top = `${y}%`;
        h.style.transform = `translate(-50%,-50%) scale(${scale})`;
      });
    };

    const renderPano = scene => {
      if (!scene) return;
      const pano = scene.querySelector('[data-kgmj-pano]');
      if (!pano) return;
      const state = roomState.get(scene.dataset.kgmjScene);

      // V30.8 room-parity upgrade: all five rooms use the same true sphere.
      if (pano.dataset.kgmjSphere === 'true') {
        const viewer = window.KGMRoom360 || window.KGMLiving360;
        if (viewer?.render) {
          viewer.render(pano, state, [...scene.querySelectorAll('[data-yaw][data-pitch]')]);
        }
        return;
      }

      // V29 behavior remains untouched for the other rooms during this prototype pass.
      const world = scene.querySelector('[data-kgmj-pano-world]');
      const img = world?.querySelector('img');
      if (!world || !img) return;
      const w = pano.clientWidth || section.clientWidth || 1200;
      const x = -w - (state.yaw / 180) * w;
      const py = state.pitch * -2.1;
      world.style.transform = `translate3d(${x}px,0,0)`;
      img.style.transform = `translateY(${py}px) scale(${state.zoom})`;
      updateHotspots(scene);
    };

    const renderActive = () => renderPano(scenes[activeIndex]);

    const showRoom = index => {
      activeIndex = (index + scenes.length) % scenes.length;
      closeProducts();
      scenes.forEach((scene,i) => scene.classList.toggle('is-active', i === activeIndex));
      const scene = scenes[activeIndex];
      // V31: every room opens from its widest/full-room view.
      roomState.set(scene.dataset.kgmjScene, stateForScene(scene));
      if (roomTitle) roomTitle.textContent = scene.dataset.title || '';
      if (roomNumber) roomNumber.textContent = scene.dataset.number || String(activeIndex+1).padStart(2,'0');
      let activeRoomButton = null;
      section.querySelectorAll('[data-kgmj-room]').forEach(btn => {
        const active = btn.dataset.kgmjRoom === scene.dataset.kgmjScene;
        btn.classList.toggle('is-active', active);
        if (active) activeRoomButton = btn;
      });
      if (activeRoomButton) {
        const bar = activeRoomButton.closest('.kgmj-roombar');
        if (bar && bar.scrollWidth > bar.clientWidth + 2) {
          requestAnimationFrame(() => {
            const left = activeRoomButton.offsetLeft;
            const right = left + activeRoomButton.offsetWidth;
            const visibleLeft = bar.scrollLeft + 8;
            const visibleRight = bar.scrollLeft + bar.clientWidth - 8;
            if (left < visibleLeft) bar.scrollTo({ left: Math.max(0, left - 8), behavior: reduce ? 'auto' : 'smooth' });
            else if (right > visibleRight) bar.scrollTo({ left: right - bar.clientWidth + 8, behavior: reduce ? 'auto' : 'smooth' });
          });
        }
      }
      setStage(explorer);
      requestAnimationFrame(() => requestAnimationFrame(renderActive));
      announce(`Entered ${scene.dataset.title}. True 360 degree view. Drag to look around and select a gold product marker.`);
    };

    // Section 4 and other homepage experiences can request a specific room directly.
    document.addEventListener('kgm:open-room', event => {
      const key = event.detail?.room;
      const idx = scenes.findIndex(scene => scene.dataset.kgmjScene === key);
      if (idx < 0) return;
      showRoom(idx);
      if (event.detail?.scroll !== false) {
        requestAnimationFrame(() => section.scrollIntoView({behavior: reduce ? 'auto' : 'smooth', block: 'start'}));
      }
    });

    // Capture critical controls before overlapping theme/header layers can swallow their clicks.
    const criticalClick = e => {
      const control = e.target.closest('[data-kgmj-close-product],[data-kgmj-back-maison],[data-kgmj-show-foyer],[data-kgmj-skip]');
      if (!control || !section.contains(control)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      if (control.matches('[data-kgmj-close-product]')) { closeProducts(true); renderActive(); return; }
      if (control.matches('[data-kgmj-back-maison]')) { startArrival(); return; }
      if (control.matches('[data-kgmj-show-foyer]')) { showFoyer(); return; }
      if (control.matches('[data-kgmj-skip]')) { showFoyer(); }
    };
    document.addEventListener('click', criticalClick, true);

    // One delegated click handler makes navigation and close controls resilient after Shopify section reloads.
    section.addEventListener('click', e => {
      const skip = e.target.closest('[data-kgmj-skip]');
      if (skip) { e.preventDefault(); e.stopPropagation(); showFoyer(); return; }

      const scrollNext = e.target.closest('[data-kgmj-scroll-next]');
      if (scrollNext) { e.preventDefault(); scrollPastJourney(); return; }

      const backMaison = e.target.closest('[data-kgmj-back-maison]');
      if (backMaison) { e.preventDefault(); e.stopPropagation(); startArrival(); return; }

      const foyerBtn = e.target.closest('[data-kgmj-show-foyer]');
      if (foyerBtn) { e.preventDefault(); e.stopPropagation(); showFoyer(); return; }

      const close = e.target.closest('[data-kgmj-close-product]');
      if (close) { e.preventDefault(); e.stopPropagation(); closeProducts(true); renderActive(); return; }

      const backdrop = e.target.closest('[data-kgmj-product-backdrop]');
      if (backdrop) { e.preventDefault(); closeProducts(true); renderActive(); return; }

      const roomBtn = e.target.closest('[data-kgmj-room]');
      if (roomBtn) {
        e.preventDefault();
        const idx = scenes.findIndex(s => s.dataset.kgmjScene === roomBtn.dataset.kgmjRoom);
        if (idx >= 0) showRoom(idx);
        return;
      }

      const hotspot = e.target.closest('[data-panel]');
      if (hotspot) {
        e.preventDefault(); e.stopPropagation();
        closeProducts();
        lastHotspot = hotspot;
        const panel = section.querySelector(`#${CSS.escape(hotspot.dataset.panel)}`);
        if (!panel) return;
        // Keep the room and product drawer fully visible instead of allowing the panel to open half off-screen.
        const rect = section.getBoundingClientRect();
        if (Math.abs(rect.top) > 8) section.scrollIntoView({behavior:'smooth', block:'start'});
        panel.classList.add('is-open');
        panel.setAttribute('aria-hidden','false');
        prepareProductViewer(panel);
        section.classList.add('has-product-open');
        document.documentElement.classList.add('kgmj-product-lock');
        document.body?.classList.add('kgmj-product-lock');
        const info = panel.querySelector('.kgmj-product__info');
        if (info) info.scrollTop = 0;
        window.setTimeout(() => panel.querySelector('[data-kgmj-close-product]')?.focus({preventScroll:true}), 80);
        announce(`Viewing ${panel.querySelector('h2')?.textContent || 'product'}`);
      }
    });

    // Keep close/back controls clickable even if a drag began underneath them.
    section.addEventListener('pointerdown', e => {
      if (e.target.closest('[data-kgmj-close-product],[data-kgmj-back-maison],[data-kgmj-show-foyer],[data-kgmj-skip]')) e.stopPropagation();
    });

    section.querySelectorAll('[data-kgmj-close-product]').forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); closeProducts(true); renderActive(); }));
    section.querySelectorAll('[data-kgmj-back-maison]').forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); startArrival(); }));

    section.querySelector('[data-kgmj-prev]')?.addEventListener('click', () => showRoom(activeIndex - 1));
    section.querySelector('[data-kgmj-next]')?.addEventListener('click', () => showRoom(activeIndex + 1));
    section.querySelector('[data-kgmj-fullscreen]')?.addEventListener('click', () => document.fullscreenElement ? document.exitFullscreen?.() : section.requestFullscreen?.());
    
    section.querySelector('[data-kgmj-zoom-out]')?.addEventListener('click', e => {
      e.preventDefault();
      const scene = scenes[activeIndex];
      const state = roomState.get(scene.dataset.kgmjScene);
      state.zoom = clamp(state.zoom - 0.12, FULLY_ZOOMED_OUT, 1.80);
      renderPano(scene);
      announce(`Zoom ${Math.round(state.zoom * 100)} percent.`);
    });
    section.querySelector('[data-kgmj-zoom-in]')?.addEventListener('click', e => {
      e.preventDefault();
      const scene = scenes[activeIndex];
      const state = roomState.get(scene.dataset.kgmjScene);
      state.zoom = clamp(state.zoom + 0.12, FULLY_ZOOMED_OUT, 1.80);
      renderPano(scene);
      announce(`Zoom ${Math.round(state.zoom * 100)} percent.`);
    });

section.querySelector('[data-kgmj-reset-view]')?.addEventListener('click', () => {
      const scene = scenes[activeIndex];
      roomState.set(scene.dataset.kgmjScene,stateForScene(scene));
      renderActive();
    });

    scenes.forEach(scene => {
      const pano = scene.querySelector('[data-kgmj-pano]');
      if (!pano) return;
      // The shared true-sphere viewer owns pointer, keyboard and wheel controls.
      if (pano.dataset.kgmjSphere === 'true') return;
      let dragging = false, sx = 0, sy = 0, startYaw = 0, startPitch = 0;
      const pointers = new Map();
      let pinchDistance = 0, pinchZoom = 1;
      pano.addEventListener('pointerdown', e => {
        if (e.target.closest('button,a,input,select,textarea')) return;
        pointers.set(e.pointerId,{x:e.clientX,y:e.clientY});
        const state = roomState.get(scene.dataset.kgmjScene);
        if (pointers.size === 1) {
          dragging = true; sx = e.clientX; sy = e.clientY; startYaw = state.yaw; startPitch = state.pitch;
          pano.classList.add('is-dragging');
        } else if (pointers.size === 2) {
          const pts=[...pointers.values()]; pinchDistance=Math.hypot(pts[0].x-pts[1].x,pts[0].y-pts[1].y); pinchZoom=state.zoom; dragging=false;
        }
      });
      pano.addEventListener('pointermove', e => {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId,{x:e.clientX,y:e.clientY});
        const state = roomState.get(scene.dataset.kgmjScene);
        if (pointers.size >= 2) {
          const pts=[...pointers.values()].slice(0,2); const d=Math.hypot(pts[0].x-pts[1].x,pts[0].y-pts[1].y);
          if (pinchDistance > 0) state.zoom=clamp(pinchZoom*(d/pinchDistance),FULLY_ZOOMED_OUT,1.80);
          renderPano(scene); return;
        }
        if (!dragging) return;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        state.yaw = norm(startYaw + dx * -0.19);
        state.pitch = clamp(startPitch + dy * 0.07, -13, 13);
        renderPano(scene);
      });
      const stop = e => {
        pointers.delete(e.pointerId);
        if (pointers.size === 0) { dragging=false; pano.classList.remove('is-dragging'); }
        else if (pointers.size === 1) { const pt=[...pointers.values()][0]; const state=roomState.get(scene.dataset.kgmjScene); dragging=true; sx=pt.x; sy=pt.y; startYaw=state.yaw; startPitch=state.pitch; }
      };
      pano.addEventListener('pointerup', stop); pano.addEventListener('pointercancel', stop);

      // Normal wheel = normal website scrolling. Ctrl/Cmd + wheel = room zoom.
      pano.addEventListener('wheel', e => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const state = roomState.get(scene.dataset.kgmjScene);
        state.zoom = clamp(state.zoom - e.deltaY * .00065, FULLY_ZOOMED_OUT, 1.80);
        renderPano(scene);
      }, {passive:false});
      pano.addEventListener('dblclick', () => { roomState.set(scene.dataset.kgmjScene,stateForScene(scene)); renderPano(scene); });
    });

    section.querySelectorAll('[data-kgmj-spin-stage]').forEach(stage => {
      const obj = stage.querySelector('.kgmj-product__spin-object'); if (!obj) return;
      let down=false,startX=0,startY=0,ry=-18,rx=-5;
      stage.addEventListener('pointerdown',e=>{
        if (e.target.closest('button,a,input,select,textarea')) return;
        down=true;startX=e.clientX;startY=e.clientY;obj.style.animation='none';
      });
      stage.addEventListener('pointermove',e=>{if(!down)return;const dx=e.clientX-startX,dy=e.clientY-startY;obj.style.setProperty('--ry',`${ry+dx*.45}deg`);obj.style.setProperty('--rx',`${Math.max(-25,Math.min(25,rx-dy*.25))}deg`);});
      stage.addEventListener('pointerup',e=>{if(!down)return;down=false;ry+=(e.clientX-startX)*.45;rx=Math.max(-25,Math.min(25,rx-(e.clientY-startY)*.25));});
      stage.addEventListener('pointercancel',()=>{down=false;});
      stage.addEventListener('wheel',e=>{
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const current=Number(obj.dataset.scale||1);const next=Math.max(.78,Math.min(1.45,current-e.deltaY*.0008));obj.dataset.scale=next;obj.style.scale=next;
      },{passive:false});
    });

    window.addEventListener('resize', () => { if (explorer?.classList.contains('is-active')) renderActive(); }, {passive:true});
    document.addEventListener('keydown', e => {
      if (!section.matches(':hover') && !section.contains(document.activeElement)) return;
      if (e.key === 'Escape') {
        if (products.some(p=>p.classList.contains('is-open'))) { closeProducts(true); renderActive(); }
        else if (explorer?.classList.contains('is-active')) showFoyer();
        return;
      }
      if (explorer?.classList.contains('is-active') && !products.some(p=>p.classList.contains('is-open'))) {
        if (e.key === 'ArrowRight') showRoom(activeIndex+1);
        if (e.key === 'ArrowLeft') showRoom(activeIndex-1);
      }
    });
  });

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => init()) : init();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
